import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Table, Button, Card, Space, Tag, message } from 'antd';
import { EditOutlined, ShopOutlined, DownloadOutlined } from '@ant-design/icons';
import { exportToExcel } from '../../utils/excelExport';
import { useStores } from '../../stores';
import BackButton from '../../components/BackButton';

const ProductQueue = observer(() => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { adminProductStore } = useStores();

  useEffect(() => {
    loadQueue();
  }, []);

  const loadQueue = async () => {
    try {
      await adminProductStore.fetchProductQueue();
    } catch (error) {
      message.error(t('admin.productQueue.loadFailed'));
    }
  };

  const handleExport = () => {
    const exportColumns = [
      { title: 'ID', dataIndex: 'id' },
      { title: t('admin.productQueue.nameEn'), dataIndex: 'name_en' },
      { title: t('admin.productQueue.nameEl'), dataIndex: 'name_el' },
      {
        title: t('admin.products.category'),
        dataIndex: 'category',
        exportRender: (cat) => cat?.name_en || ''
      },
      {
        title: t('admin.products.partnersCount'),
        dataIndex: 'variants',
        exportRender: (variants) => variants ?
          new Set(variants.map(v => v.partner?.id).filter(Boolean)).size : 0
      },
      {
        title: t('admin.products.variantsCount'),
        dataIndex: 'variants',
        exportRender: (variants) => variants?.length || 0
      },
      {
        title: t('common.status'),
        dataIndex: 'id',
        exportRender: (_, record) => {
          const hasImages = record.image1 || record.image2 || record.image3 || record.image4;
          const hasDescriptions = record.description_en || record.description_el;
          return (hasImages && hasDescriptions) ? t('admin.productQueue.partiallyComplete') : t('admin.productQueue.awaitingMarketing');
        }
      }
    ];
    exportToExcel(adminProductStore.queueProducts, exportColumns, 'product_queue', t('admin.productQueue.title'));
    message.success(t('admin.productQueue.exportSuccess'));
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: t('admin.productQueue.productName'),
      dataIndex: 'name_en',
      key: 'name_en',
      render: (text, record) => (
        <div>
          <div><strong>EN:</strong> {text || <em>{t('admin.products.notSet')}</em>}</div>
          <div><strong>EL:</strong> {record.name_el || <em>{t('admin.products.notSet')}</em>}</div>
        </div>
      ),
    },
    {
      title: t('admin.products.category'),
      dataIndex: 'category',
      key: 'category',
      render: (category) => category ? (
        <Tag color="blue">{i18n.language === 'el' ? (category.name_el || category.name_en) : category.name_en}</Tag>
      ) : (
        <Tag color="red">{t('admin.products.notSet')}</Tag>
      ),
    },
    {
      title: t('admin.productQueue.partnersSelling'),
      dataIndex: 'variants',
      key: 'partners',
      render: (variants) => {
        const partnerCount = variants ?
          new Set(variants.map(v => v.partner?.id).filter(Boolean)).size : 0;
        return (
          <Space>
            <ShopOutlined />
            <span>{partnerCount}</span>
          </Space>
        );
      },
    },
    {
      title: t('admin.products.variants'),
      dataIndex: 'variants',
      key: 'variants',
      render: (variants) => variants?.length || 0,
    },
    {
      title: t('common.status'),
      key: 'status',
      render: (_, record) => {
        const hasImages = record.image1 || record.image2 || record.image3 || record.image4;
        const hasDescriptions = record.description_en || record.description_el;

        if (hasImages && hasDescriptions) {
          return <Tag color="orange">{t('admin.productQueue.partiallyComplete')}</Tag>;
        }
        return <Tag color="red">{t('admin.productQueue.awaitingMarketing')}</Tag>;
      },
    },
    {
      title: t('common.actions'),
      key: 'action',
      render: (_, record) => (
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => navigate(`/admin/products/enrich/${record.id}`)}
        >
          {t('admin.productQueue.enrich')}
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <BackButton to="/admin/dashboard" label={t('admin.backToDashboard')} />
      <Card
        title={
          <Space>
            <span style={{ fontSize: '20px', fontWeight: 'bold' }}>
              {t('admin.productQueue.title')}
            </span>
            <Tag color="orange">{t('admin.productQueue.pending', { count: adminProductStore.queueProducts.length })}</Tag>
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              disabled={!adminProductStore.queueProducts.length}
            >
              {t('admin.exportExcel')}
            </Button>
            <Button onClick={loadQueue}>{t('common.refresh')}</Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={adminProductStore.queueProducts}
          rowKey="id"
          loading={adminProductStore.loading}
          pagination={{ pageSize: 20 }}
        />
      </Card>
    </div>
  );
});

export default ProductQueue;
